import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { AlertRuleBody } from '../api/types'
import { testReadingRule, type RuleTestResult } from '../lib/alarmSimulation'
import { formatDateTime, fromLocalInput, toLocalInput } from '../lib/time'
import { names } from '../lib/quality'

/** Explicit point-in-time condition test. It cannot write to the alarm API. */
export function AlarmTestPanel({ rule, unit, valid }: { rule: AlertRuleBody; unit: string; valid: boolean }) {
  const [testAt, setTestAt] = useState(() => toLocalInput(new Date()))
  const [days, setDays] = useState(7)
  const [result, setResult] = useState<RuleTestResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const request = useRef(0)
  const identity = JSON.stringify([rule.station, rule.parameter, rule.condition, rule.warn_level,
    rule.alarm_level, rule.hold_s, testAt, days, valid])
  useEffect(() => {
    request.current += 1; setResult(null); setError(''); setBusy(false)
    return () => { request.current += 1 }
  }, [identity])

  const run = async () => {
    const at = fromLocalInput(testAt)
    if (!valid || !rule.parameter || !Number.isFinite(at.getTime())) return
    const token = ++request.current
    setBusy(true); setError(''); setResult(null)
    try {
      const response = await api.series({ stations: [], parameters: [], pairs: [[rule.station, rule.parameter]],
        from: new Date(at.getTime() - days * 86_400_000), to: new Date(at.getTime() + 1), includeQuality: true })
      if (request.current !== token) return
      const series = response.series.find((s) => s.station === rule.station && s.parameter === rule.parameter)
      setResult(testReadingRule(rule, series?.points ?? [], at, response.truncated))
    } catch (failure) {
      if (request.current === token) setError(failure instanceof Error ? failure.message : 'The test could not be completed.')
    } finally { if (request.current === token) setBusy(false) }
  }
  const metricUnit = rule.condition === 'rate' ? `${unit}/day` : rule.condition === 'stale' ? 'min' : unit
  const number = (value: number) => Number.isInteger(value) ? String(value) : String(Number(value.toPrecision(10)))
  return <details className="aw-test">
    <summary>Test against readings</summary>
    <div className="aw-test-body">
      <div className="aw-test-inputs">
        <label className="field"><span>Test at · Tbilisi</span><input type="datetime-local" value={testAt}
          onChange={(event) => setTestAt(event.target.value)} /></label>
        <label className="field"><span>Lookback</span><select value={days} onChange={(event) => setDays(Number(event.target.value))}>
          <option value={3}>3 days</option><option value={7}>7 days</option><option value={30}>30 days</option>
        </select></label>
        <button type="button" className="button" disabled={busy || !valid || !Number.isFinite(fromLocalInput(testAt).getTime())}
          onClick={run}>{busy ? 'Testing…' : 'Run test'}</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {result && <div className={`aw-test-result ${result.evaluated ? `aw-test-${result.level ?? 'clear'}` : 'aw-test-unknown'}`} role="status">
        <strong>{result.evaluated ? result.reason : 'Not evaluated'}</strong>
        {!result.evaluated ? <span>{result.reason}</span> : <>
          <span className="aw-test-metric">{number(result.metric!)} <small>{metricUnit}</small></span>
          <span>{result.considered} observation{result.considered === 1 ? '' : 's'} used{result.excluded ? ` · ${result.excluded} missing or invalid excluded` : ''}</span>
          {result.newest && <span>Latest: {String(result.newest.value)} {unit} · {formatDateTime(result.newest.ts)}</span>}
          {result.earlier && <span>Baseline: {String(result.earlier.value)} {unit} · {formatDateTime(result.earlier.ts)}</span>}
          {result.newest && names(result.newest.quality).length > 0 && <span>QC: {names(result.newest.quality).join(', ')}</span>}
          {result.held && result.newestLevel !== result.level && <span>Newest reading alone: {result.newestLevel ?? 'no trigger'}.</span>}
        </>}
      </div>}
      <small className="aw-test-note">Read-only condition test. No event or email is created.</small>
    </div>
  </details>
}
